import { create } from 'zustand'
import { shallow } from 'zustand/shallow'

import { UserRole } from '@brickup/shared/enums'

/**
 * The roles the screens read, for the controls they show or hide by them.
 *
 * Two sets, because root may try others on: `realRoles` is what the server
 * signed in and decides whether the switcher is offered at all,
 * `assumedRoles` is what root is trying on. Every screen reads the effective
 * set through `selectUserRoles` — roles being tried on are simply the roles
 * the app has.
 *
 * Deliberately not persisted: reloading is the way back out of a role that
 * turned out to lock the page it was assumed on.
 */
type AppState = {
  /** What the server signed in. */
  realRoles: readonly UserRole[] | null
  /** The roles root is trying on, or null while they are themselves. */
  assumedRoles: readonly UserRole[] | null
  setAuthenticated: (realRoles: readonly UserRole[]) => void
  assumeRoles: (assumedRoles: readonly UserRole[] | null) => void
  clearAuthenticationState: () => void
}

const SIGNED_OUT = {
  realRoles: null,
  assumedRoles: null,
} satisfies Partial<AppState>

export const useAppStore = create<AppState>()((set, get) => ({
  ...SIGNED_OUT,
  // Said on every read of the user query, not only on a sign-in, so roles
  // being tried on survive it — only somebody else signing in ends them. The
  // held array is kept while the roles are equal, so a selector reading it
  // does not re-render on every refetch.
  setAuthenticated: realRoles =>
    shallow(realRoles, get().realRoles)
      ? undefined
      : set({ realRoles, assumedRoles: null }),
  assumeRoles: assumedRoles => set({ assumedRoles }),
  clearAuthenticationState: () => set(SIGNED_OUT),
}))

/**
 * What the app treats the reader as.
 *
 * Derived rather than stored: kept as a third field it would have to be
 * recomputed by every setter that touches either of the two, and the one that
 * forgets is the one that leaves a role assumed after a sign-out.
 */
export const selectUserRoles = (state: AppState) =>
  state.assumedRoles ?? state.realRoles

/** The same answer where there is no component to select in — a route loader. */
export const currentUserRoles = () => selectUserRoles(useAppStore.getState())
